import StoreStockViewModel from 'Product/StoreStockViewModel.type';
import StoreModel from 'Stores/StoreModel.type';

interface GeolocationCoordinates {
  readonly accuracy: number;
  readonly altitude: number | null;
  readonly altitudeAccuracy: number | null;
  readonly heading: number | null;
  readonly latitude: number;
  readonly longitude: number;
  readonly speed: number | null;
}

type EpochTimeStamp = number;

// This is a standard type from TS version 4.1.x, but we use an old version of TS for our builds
interface GeolocationPosition {
  readonly coords: GeolocationCoordinates;
  readonly timestamp: EpochTimeStamp;
}

function crowFliesDistanceToStore(store: StoreModel | StoreStockViewModel, position: GeolocationPosition): number {
  const lat = 110.574 * (store.latitude - position.coords.latitude);
  const long =
    111.32 * Math.cos((position.coords.latitude * Math.PI) / 180.0) * (store.longitude - position.coords.longitude);
  return Math.sqrt(long * long + lat * lat);
}

export function sortByCrowFliesDistance(stores: StoreModel[] | StoreStockViewModel[], position: GeolocationPosition) {
  if (stores?.length < 1) {
    return stores;
  }

  return stores.sort((a: StoreStockViewModel, b: StoreStockViewModel) => {
    const aDist = crowFliesDistanceToStore(a, position);
    const bDist = crowFliesDistanceToStore(b, position);
    if (aDist === bDist) {
      return 0;
    }
    return aDist > bDist ? 1 : -1;
  });
}

async function fetchDrivingDistances(stores: StoreModel[], position: GeolocationPosition) {
  var service = new google.maps.DistanceMatrixService();
  const getDistanceMatrix = (store: StoreModel) =>
    new Promise((resolve, reject) => {
      service.getDistanceMatrix(
        {
          origins: [new google.maps.LatLng(position.coords.latitude, position.coords.longitude)],
          destinations: [new google.maps.LatLng(store.latitude, store.longitude)],
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.METRIC,
        },
        storeCallback(resolve, reject),
      );
    });

  const distanceMatrixPromises = stores.map((store) => {
    return getDistanceMatrix(store);
  });

  stores = [];

  function storeCallback(resolve: (value: unknown) => void, reject: (value: unknown) => void) {
    return (response: google.maps.DistanceMatrixResponse, status: google.maps.DistanceMatrixStatus) => {
      if (status !== google.maps.DistanceMatrixStatus.OK) {
        reject(new Error(`Request to Distance Matrix API failed: ${status}`));
        return;
      }
      if (response.rows.length <= 0) {
        reject(new Error(`Request to Distance Matrix API failed: Empty rows`));
        return;
      }

      if (response.rows[0].elements.length <= 0) {
        reject(new Error(`Request to Distance Matrix API failed: Empty elements`));
        return;
      }

      resolve(response.rows[0].elements[0].distance.value);
    };
  }

  return Promise.all(distanceMatrixPromises);
}

function filterByCrowFliesDistance(stores: StoreModel[], position: GeolocationPosition, distance: number) {
  return stores.filter((store: StoreModel) => {
    return crowFliesDistanceToStore(store, position) <= distance;
  });
}

export async function filterByDistance(stores: StoreModel[], position: GeolocationPosition, distance: number) {
  return fetchDrivingDistances(stores, position)
    .then((storeDistances) => {
      return stores.filter((store, i) => {
        return storeDistances[i] as number <= distance * 1000;
      });
    })
    .catch(() => {
      return filterByCrowFliesDistance(stores, position, distance);
    });
}
